import { baseLocale, getLocale, locales, localizeHref, type Locale } from '../paraglide/runtime';

const ORIGIN = 'https://getanyapi.com';

/** Open Graph wants territory form (`zh_CN`), not the BCP 47 tag the page uses. */
const OG_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  'zh-Hans': 'zh_CN',
  'pt-BR': 'pt_BR',
  es: 'es_LA',
  fr: 'fr_FR',
  de: 'de_DE',
  ru: 'ru_RU',
  it: 'it_IT',
};

/** The absolute URL of an English path in one locale. */
export const localizedUrl = (path: string, locale: Locale = getLocale()): string =>
  ORIGIN + localizeHref(path, { locale });

/** Whether an English path is served in any locale besides English. */
export const isTranslated = (path: string): boolean =>
  locales.some((locale) => locale !== baseLocale && localizeHref(path, { locale }) !== path);

/**
 * Every language version of an English path: one per locale when it is
 * translated, the English path alone when it is not. The sitemap lists these
 * and the build prerenders them, so both take them from here.
 */
export const languageVersions = (path: string): { locale: Locale; path: string }[] =>
  isTranslated(path)
    ? locales.map((locale) => ({ locale, path: localizeHref(path, { locale }) }))
    : [{ locale: baseLocale, path }];

/**
 * The canonical link for an English path, plus its language alternates when it
 * is translated.
 *
 * Each version names itself as canonical, never the English page: a translated
 * page that points its canonical at English tells Google it is a duplicate. The
 * alternates list every version including this one, plus `x-default` for
 * readers whose language matches none (Search Central, "Tell Google about
 * localized versions of your page"). An untranslated path gets the canonical
 * alone, exactly what it rendered before translations existed.
 */
export const localizedLinks = (path: string) => {
  const canonical = { rel: 'canonical', href: localizedUrl(path) };
  if (!isTranslated(path)) return [canonical];
  return [
    canonical,
    ...locales.map((locale) => ({ rel: 'alternate', hrefLang: locale, href: localizedUrl(path, locale) })),
    { rel: 'alternate', hrefLang: 'x-default', href: localizedUrl(path, baseLocale) },
  ];
};

/** `og:locale` for the current page, in the territory form Open Graph wants. */
export const ogLocale = (): string => OG_LOCALE[getLocale()];
